import React, { useState, useRef, useEffect } from 'react'
import { Minus, Plus, Scissors, Magnet, ChevronDown } from 'lucide-react'
import type { Segment, Transcript, MusicTrack } from '../types'
import { CAPTION_STYLES, CaptionPreview, captionStyleSpec } from '../captionStyles'
import type { CaptionStyle } from '../captionStyles'
export type { CaptionStyle } from '../captionStyles'

interface TimelineProps {
  segments: Segment[]
  selectedSegments: string[]
  currentTime: number
  duration: number
  onSegmentSelect: (segmentId: string, multiSelect: boolean) => void
  onSegmentUpdate: (segmentId: string, updates: Partial<Segment>) => void
  onSeek: (time: number) => void
  transcript?: Transcript | null
  musicTrack?: MusicTrack | null
  voiceClips?: { id: string; url: string; text: string; start: number; duration: number }[]
  onVoiceDrop?: (clip: { url: string; text: string; duration?: number }, start: number) => void
  musicClips?: { id: string; url: string; text: string; start: number; duration: number }[]
  sfxClips?: { id: string; url: string; text: string; start: number; duration: number }[]
  onMusicDrop?: (clip: { url: string; text: string; duration?: number }, start: number) => void
  onSfxDrop?: (clip: { url: string; text: string; duration?: number }, start: number) => void
  imageClips?: { id: string; url: string; text: string; start: number; duration: number }[]
  onImageDrop?: (clip: { url: string; text: string; duration?: number }, start: number) => void
  onClipUpdate?: (kind: string, id: string, updates: { start?: number; duration?: number; offset?: number }) => void
  onClipSelect?: (kind: string, id: string) => void
  selectedClip?: { kind: string; id: string } | null
  captionStyle?: CaptionStyle
  onCaptionStyleChange?: (style: CaptionStyle) => void
  karaokeMode?: boolean
  onKaraokeToggle?: () => void
}

const LANE_H = 40 // px, keep in sync with the label column
const RULER_H = 24

export function Timeline({
  segments,
  selectedSegments,
  currentTime,
  duration,
  onSegmentSelect,
  onSegmentUpdate,
  onSeek,
  transcript,
  musicTrack,
  voiceClips = [],
  onVoiceDrop,
  musicClips = [],
  sfxClips = [],
  onMusicDrop,
  onSfxDrop,
  imageClips = [],
  onImageDrop,
  onClipUpdate,
  onClipSelect,
  selectedClip,
  captionStyle = 'dynamic_viral',
  onCaptionStyleChange,
  karaokeMode,
  onKaraokeToggle,
}: TimelineProps) {
  const innerRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  const [containerW, setContainerW] = useState(900)
  const [capOpen, setCapOpen] = useState(false)

  // Drag state (Video clips)
  const [isDragging, setIsDragging] = useState(false)
  const [dragType, setDragType] = useState<'move' | 'resize-start' | 'resize-end' | null>(null)
  const [dragSegment, setDragSegment] = useState<string | null>(null)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartTime, setDragStartTime] = useState(0)
  const [clipDrag, setClipDrag] = useState<{ kind: string; id: string; type: 'move' | 'l' | 'r'; x0: number; start0: number; dur0: number; off0: number } | null>(null)

  const eff = duration > 0
    ? duration
    : segments.length > 0
      ? Math.max(...segments.map(s => s.end)) * 1.1
      : 60

  const pct = (t: number) => `${Math.max(0, Math.min(100, (t / eff) * 100))}%`
  const spanPct = (a: number, b: number) => {
    const realPct = ((b - a) / eff) * 100
    const minPct = (7 / (containerW * zoom)) * 100 // ~7px minimum hit area, zoom-aware
    return `${Math.min(100, Math.max(minPct, realPct))}%`
  }
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`

  const innerWidthPx = () => innerRef.current?.getBoundingClientRect().width || 800

  // ---- Zoom: duration-aware ceiling + cursor-anchored wheel zoom ----
  const TARGET_PXPS = 60 // px/sec reachable at max zoom, regardless of clip length
  const maxZoom = Math.min(300, Math.max(8, Math.ceil((TARGET_PXPS * eff) / Math.max(1, containerW))))
  const pxPerSec = (containerW * zoom) / eff
  const zoomRef = useRef(zoom); zoomRef.current = zoom
  const maxZoomRef = useRef(maxZoom); maxZoomRef.current = maxZoom

  // Zoom while keeping the time under the anchor (cursor, or viewport centre) fixed.
  const applyZoom = (nz: number, anchorClientX?: number) => {
    const el = scrollRef.current, inner = innerRef.current
    const clamped = Math.min(maxZoomRef.current, Math.max(1, +nz.toFixed(3)))
    if (!el || !inner) { setZoom(clamped); return }
    const vpLeft = el.getBoundingClientRect().left
    const ax = anchorClientX != null ? anchorClientX - vpLeft : el.clientWidth / 2
    const timeAtAnchor = ((el.scrollLeft + ax) / inner.getBoundingClientRect().width) * eff
    setZoom(clamped)
    requestAnimationFrame(() => {
      const nw = inner.getBoundingClientRect().width
      el.scrollLeft = (timeAtAnchor / eff) * nw - ax
    })
  }
  const applyZoomRef = useRef(applyZoom); applyZoomRef.current = applyZoom

  // Measure the scroll viewport so the zoom ceiling can be duration-aware.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const measure = () => setContainerW(el.clientWidth || 900)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Mouse-wheel zoom (native non-passive listener so preventDefault sticks).
  // shift/alt-wheel is left alone so the native horizontal scroll still works.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey || e.altKey) return
      e.preventDefault()
      const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2
      applyZoomRef.current(zoomRef.current * factor, e.clientX)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  const handleSeekClick = (e: React.MouseEvent) => {
    const rect = innerRef.current?.getBoundingClientRect()
    if (!rect) return
    const t = ((e.clientX - rect.left) / rect.width) * eff
    onSeek(Math.max(0, Math.min(eff, t)))
  }

  // Pointer events, not mouse events, everywhere below. One API covers mouse, pen
  // and finger, so the timeline is draggable on a touchscreen without a second code
  // path — and mouse behaviour is unchanged, because a mouse IS a pointer.
  const handleDragStart = (e: React.PointerEvent, segmentId: string, type: 'move' | 'resize-start' | 'resize-end') => {
    e.stopPropagation()
    setIsDragging(true)
    setDragType(type)
    setDragSegment(segmentId)
    setDragStartX(e.clientX)
    const seg = segments.find(s => s.id === segmentId)
    if (seg) setDragStartTime(type === 'resize-start' ? seg.start : seg.end)
  }

  const handleDragMove = (e: PointerEvent) => {
    if (!isDragging || !dragSegment || !dragType) return
    const deltaTime = ((e.clientX - dragStartX) / innerWidthPx()) * eff
    const seg = segments.find(s => s.id === dragSegment)
    if (!seg) return
    let updates: Partial<Segment> = {}
    if (dragType === 'move') {
      const len = seg.end - seg.start
      const newStart = Math.max(0, Math.min(eff - len, seg.start + deltaTime))
      updates = { start: newStart, end: newStart + len }
    } else if (dragType === 'resize-start') {
      updates = { start: Math.max(0, Math.min(seg.end - 1, dragStartTime + deltaTime)) }
    } else if (dragType === 'resize-end') {
      updates = { end: Math.max(seg.start + 1, Math.min(eff, dragStartTime + deltaTime)) }
    }
    onSegmentUpdate(dragSegment, updates)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    setDragType(null)
    setDragSegment(null)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('pointermove', handleDragMove)
      document.addEventListener('pointerup', handleDragEnd)
      // Touch gestures can be taken away mid-drag — a system edge-swipe, an
      // incoming call. Without this the drag state sticks and the segment follows
      // the finger forever after the gesture is gone.
      document.addEventListener('pointercancel', handleDragEnd)
      return () => {
        document.removeEventListener('pointermove', handleDragMove)
        document.removeEventListener('pointerup', handleDragEnd)
        document.removeEventListener('pointercancel', handleDragEnd)
      }
    }
  }, [isDragging, dragSegment, dragType, dragStartX, dragStartTime])

  const startClipDrag = (e: React.PointerEvent, kind: string, id: string, type: 'move' | 'l' | 'r', c: { start: number; duration: number; offset?: number }) => {
    e.stopPropagation()
    setClipDrag({ kind, id, type, x0: e.clientX, start0: c.start, dur0: c.duration, off0: c.offset || 0 })
  }
  useEffect(() => {
    if (!clipDrag) return
    const onMove = (e: PointerEvent) => {
      const dt = ((e.clientX - clipDrag.x0) / innerWidthPx()) * eff
      let u: { start?: number; duration?: number; offset?: number }
      if (clipDrag.type === 'move') u = { start: Math.max(0, clipDrag.start0 + dt) }
      else if (clipDrag.type === 'r') u = { duration: Math.max(0.5, clipDrag.dur0 + dt) }
      else {
        const ns = Math.max(0, Math.min(clipDrag.start0 + clipDrag.dur0 - 0.5, clipDrag.start0 + dt))
        const delta = ns - clipDrag.start0
        u = { start: ns, duration: clipDrag.dur0 - delta, offset: Math.max(0, clipDrag.off0 + delta) }
      }
      onClipUpdate?.(clipDrag.kind, clipDrag.id, u)
    }
    const onUp = () => setClipDrag(null)
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onUp)
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onUp)
    }
  }, [clipDrag])

  // Ruler markers
  const interval = eff > 600 ? 60 : eff > 300 ? 30 : eff > 120 ? 15 : 5
  const markers: number[] = []
  for (let t = 0; t <= eff; t += interval) markers.push(t)

  const TRACKS = [
    { key: 'video', name: 'Video', dot: 'bg-orange-400' },
    { key: 'captions', name: 'Captions', dot: 'bg-amber-400' },
    { key: 'voice', name: 'Voice', dot: 'bg-emerald-400' },
    { key: 'music', name: 'Music', dot: 'bg-fuchsia-400' },
    { key: 'sfx', name: 'SFX', dot: 'bg-sky-400' },
    { key: 'broll', name: 'B-roll', dot: 'bg-orange-400' },
  ]

  const sentences = transcript?.sentences || []

  const clipEl = (kind: string, v: { id: string; text: string; start: number; duration: number; offset?: number }, cls: string, icon: string) => {
    const sel = !!selectedClip && selectedClip.kind === kind && selectedClip.id === v.id
    // touchAction 'none' on anything draggable below: without it the browser claims
    // the gesture for page scrolling and the clip never moves under a finger.
    return (
      <div key={v.id}
        className={`absolute top-1 bottom-1 rounded ${cls} text-[10px] px-1.5 flex items-center overflow-hidden cursor-grab active:cursor-grabbing ${sel ? 'ring-2 ring-white/80 z-10' : ''}`}
        style={{ left: pct(v.start), width: spanPct(v.start, v.start + (v.duration || 3)), touchAction: 'none' }}
        title={v.text}
        onClick={(e) => { e.stopPropagation(); onClipSelect?.(kind, v.id) }}
        onPointerDown={(e) => startClipDrag(e, kind, v.id, 'move', v)}>
        <div className="tl-handle absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40" onPointerDown={(e) => startClipDrag(e, kind, v.id, 'l', v)} />
        <span className="truncate pointer-events-none">{icon} {v.text}</span>
        <div className="tl-handle absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/40" onPointerDown={(e) => startClipDrag(e, kind, v.id, 'r', v)} />
      </div>
    )
  }

  return (
    <div className="w-full text-xs select-none">
      {/* Header: track count + tools + zoom */}
      <div className="flex items-center justify-between mb-1 px-1 text-[11px] text-gray-500">
        <span>{TRACKS.length} tracks · {fmt(eff)}</span>
        <div className="flex items-center gap-2">
          <Scissors size={13} className="text-gray-500 hover:text-white cursor-default" />
          <Magnet size={13} className="text-gray-500 hover:text-white cursor-default" />

          {/* Caption engine picker — the style/engine that gets burned in on export */}
          {onCaptionStyleChange && (
            <div className="relative">
              <button
                onClick={() => setCapOpen(o => !o)}
                title="Choose the caption engine that gets burned into the export"
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] transition-colors ${capOpen ? 'border-indigo-500 text-white' : 'border-white/10 text-gray-400 hover:text-white hover:border-white/30'}`}
              >
                <span>💬 {captionStyleSpec(captionStyle).short}{karaokeMode ? ' · karaoke' : ''}</span>
                <ChevronDown size={11} />
              </button>
              {capOpen && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setCapOpen(false)} />
                  <div className="absolute right-0 top-6 z-30 w-80 rounded-lg border border-white/10 bg-[#12141c] shadow-xl text-left overflow-hidden">
                    <div className="text-[9px] uppercase tracking-wide text-gray-500 px-2 pt-2 pb-1">Caption engine — burned into the export</div>
                    <div className="max-h-72 overflow-y-auto px-1.5 pb-1.5 space-y-1">
                      {CAPTION_STYLES.map(s => (
                        <button
                          key={s.value}
                          onClick={() => { onCaptionStyleChange(s.value); setCapOpen(false) }}
                          className={`w-full flex items-center gap-2 px-1.5 py-1 rounded ${captionStyle === s.value ? 'bg-indigo-500/25 ring-1 ring-indigo-400/50' : 'hover:bg-white/5'}`}
                        >
                          <span className="shrink-0 w-[96px] h-[42px] rounded bg-black flex items-end justify-center overflow-hidden px-1 pb-1">
                            <CaptionPreview spec={s} size={11} />
                          </span>
                          <span className="min-w-0 text-left">
                            <span className="block text-[11px] font-medium text-white leading-tight">{s.label}</span>
                            <span className="block text-[9px] text-gray-500 leading-tight">{s.desc}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => onKaraokeToggle?.()}
                      className="w-full flex items-center justify-between px-2 py-1.5 border-t border-white/10 text-[11px] text-gray-300 hover:bg-white/5"
                    >
                      <span>Word-by-word karaoke</span>
                      <span className={`w-7 h-4 rounded-full relative transition-colors ${karaokeMode ? 'bg-indigo-500' : 'bg-gray-600'}`}>
                        <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${karaokeMode ? 'left-[14px]' : 'left-0.5'}`} />
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-1">
            <button title="Zoom out" onClick={() => applyZoom(zoom / 1.3)} className="p-0.5 hover:text-white"><Minus size={12} /></button>
            <input type="range" min={1} max={maxZoom} step={0.1} value={Math.min(zoom, maxZoom)}
              onChange={e => applyZoom(Number(e.target.value))}
              className="w-28 accent-indigo-500" title="Zoom the timeline — or scroll the mouse wheel over the tracks" />
            <button title="Zoom in" onClick={() => applyZoom(zoom * 1.3)} className="p-0.5 hover:text-white"><Plus size={12} /></button>
            <button title="Fit the whole timeline" onClick={() => applyZoom(1)}
              className="px-1 leading-4 text-[10px] rounded border border-white/10 hover:text-white hover:border-white/30">Fit</button>
            <span className="tabular-nums text-[10px] text-gray-500 w-12 text-right" title="pixels per second">
              {zoom <= 1.01 ? 'fit' : `${Math.round(pxPerSec)}px/s`}</span>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Label column */}
        <div className="w-16 shrink-0">
          <div style={{ height: RULER_H }} />
          {TRACKS.map(t => (
            <div key={t.key} style={{ height: LANE_H }} className="flex items-center gap-1.5 pr-2 border-b border-white/5">
              <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
              <span className="text-[11px] text-gray-400">{t.name}</span>
            </div>
          ))}
        </div>

        {/* Scrollable track area */}
        <div ref={scrollRef} className="flex-1 overflow-x-auto overflow-y-hidden">
          <div ref={innerRef} className="relative" style={{ width: `${zoom * 100}%`, minWidth: '100%' }}>
            {/* Ruler */}
            <div className="relative border-b border-white/10" style={{ height: RULER_H }} onClick={handleSeekClick}>
              {markers.map(t => (
                <div key={t} className="absolute top-0 bottom-0 flex flex-col items-start" style={{ left: pct(t) }}>
                  <span className="text-[9px] text-gray-500 pl-1 leading-4">{fmt(t)}</span>
                </div>
              ))}
            </div>

            {/* Grid lines across lanes */}
            {markers.map(t => (
              <div key={`g${t}`} className="absolute w-px bg-white/5 pointer-events-none" style={{ left: pct(t), top: RULER_H, bottom: 0 }} />
            ))}

            {/* Playhead */}
            <div className="absolute w-0.5 bg-red-500 z-20 pointer-events-none" style={{ left: pct(currentTime), top: 0, bottom: 0 }}>
              <div className="w-2 h-2 -ml-[3px] -mt-0.5 rotate-45 bg-red-500" />
            </div>

            {/* VIDEO lane */}
            <div className="relative border-b border-white/5" style={{ height: LANE_H }} onClick={handleSeekClick}>
              {segments.map((seg, i) => {
                const selected = selectedSegments.includes(seg.id)
                const n = i + 1
                return (
                  <div
                    key={seg.id}
                    className={`absolute top-1 bottom-1 rounded-md overflow-hidden cursor-grab active:cursor-grabbing border transition-shadow ${selected ? 'ring-2 ring-purple-400 border-purple-300 shadow-[0_0_12px_rgba(168,85,247,0.65)] z-10' : 'border-white/10 hover:border-white/30'}`}
                    style={{ left: pct(seg.start), width: spanPct(seg.start, seg.end), background: seg.color || 'linear-gradient(90deg,#f59e0b,#f97316)', touchAction: 'none' }}
                    onClick={(e) => { e.stopPropagation(); onSegmentSelect(seg.id, e.ctrlKey || e.metaKey) }}
                    onPointerDown={(e) => handleDragStart(e, seg.id, 'move')}
                    title={`Clip ${n}: ${seg.text || 'clip'}`}
                  >
                    <div className="px-1 h-full flex items-center gap-1 text-[10px] font-medium text-white/90 truncate">
                      <span className={`shrink-0 inline-flex items-center justify-center min-w-[15px] h-[15px] px-0.5 rounded text-[9px] font-bold ${selected ? 'bg-purple-500 text-white' : 'bg-black/45 text-white/85'}`}>{n}</span>
                      <span className="truncate">{seg.text ? seg.text.slice(0, 34) : 'clip'}</span>
                    </div>
                    <div className="tl-handle absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/25" onPointerDown={(e) => handleDragStart(e, seg.id, 'resize-start')} />
                    <div className="tl-handle absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-white/25" onPointerDown={(e) => handleDragStart(e, seg.id, 'resize-end')} />
                  </div>
                )
              })}
            </div>

            {/* CAPTIONS lane */}
            <div className="relative border-b border-white/5" style={{ height: LANE_H }} onClick={handleSeekClick}>
              {sentences.map((s, i) => (
                <button
                  key={s.id || i}
                  onClick={(e) => { e.stopPropagation(); onSeek(s.start) }}
                  className="absolute top-1.5 bottom-1.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] px-1 truncate hover:bg-amber-500/30"
                  style={{ left: pct(s.start), width: spanPct(s.start, s.end) }}
                  title={s.text}
                >
                  {s.text?.slice(0, 24) || '…'}
                </button>
              ))}
              {sentences.length === 0 && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600">no captions yet</span>
              )}
            </div>

            {/* VOICE lane (drop target for generated voiceovers) */}
            <div
              className="relative border-b border-white/5"
              style={{ height: LANE_H }}
              onClick={handleSeekClick}
              onDragOver={(e) => { if (onVoiceDrop && e.dataTransfer.types.includes('application/x-voice-clip')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
              onDrop={(e) => {
                if (!onVoiceDrop) return
                const raw = e.dataTransfer.getData('application/x-voice-clip')
                if (!raw) return
                e.preventDefault()
                try {
                  const clip = JSON.parse(raw)
                  const rect = innerRef.current?.getBoundingClientRect()
                  const start = rect ? Math.max(0, ((e.clientX - rect.left) / rect.width) * eff) : 0
                  onVoiceDrop(clip, start)
                } catch {}
              }}
            >
              {voiceClips.map(v => clipEl('voice', v, 'bg-gradient-to-r from-emerald-600/40 to-teal-600/40 border border-emerald-500/40 text-emerald-100', '🎙'))}
              {voiceClips.length === 0 && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600">drop or add a voiceover here</span>
              )}
            </div>

            {/* MUSIC lane (drop target) */}
            <div className="relative border-b border-white/5" style={{ height: LANE_H }} onClick={handleSeekClick}
              onDragOver={(e) => { if (onMusicDrop && e.dataTransfer.types.includes('application/x-music-clip')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
              onDrop={(e) => { if (!onMusicDrop) return; const raw = e.dataTransfer.getData('application/x-music-clip'); if (!raw) return; e.preventDefault(); try { const clip = JSON.parse(raw); const rect = innerRef.current?.getBoundingClientRect(); const start = rect ? Math.max(0, ((e.clientX - rect.left) / rect.width) * eff) : 0; onMusicDrop(clip, start) } catch {} }}>
              {musicClips.map(m => clipEl('music', m, 'bg-gradient-to-r from-fuchsia-600/40 to-purple-600/40 border border-fuchsia-500/40 text-fuchsia-100', '♪'))}
              {musicClips.length === 0 && (musicTrack
                ? <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-fuchsia-300/70">♪ {musicTrack.title}</span>
                : <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600">drop or add music here</span>)}
            </div>

            {/* SFX lane (drop target) */}
            <div className="relative border-b border-white/5" style={{ height: LANE_H }} onClick={handleSeekClick}
              onDragOver={(e) => { if (onSfxDrop && e.dataTransfer.types.includes('application/x-sfx-clip')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
              onDrop={(e) => { if (!onSfxDrop) return; const raw = e.dataTransfer.getData('application/x-sfx-clip'); if (!raw) return; e.preventDefault(); try { const clip = JSON.parse(raw); const rect = innerRef.current?.getBoundingClientRect(); const start = rect ? Math.max(0, ((e.clientX - rect.left) / rect.width) * eff) : 0; onSfxDrop(clip, start) } catch {} }}>
              {sfxClips.map(x => clipEl('sfx', x, 'bg-gradient-to-r from-sky-600/40 to-indigo-600/40 border border-sky-500/40 text-sky-100', '🔊'))}
              {sfxClips.length === 0 && (<span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600">drop or add SFX here</span>)}
            </div>

            {/* B-ROLL lane (image overlays composited on export) */}
            <div className="relative border-b border-white/5" style={{ height: LANE_H }} onClick={handleSeekClick}
              onDragOver={(e) => { if (onImageDrop && e.dataTransfer.types.includes('application/x-image-clip')) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' } }}
              onDrop={(e) => { if (!onImageDrop) return; const raw = e.dataTransfer.getData('application/x-image-clip'); if (!raw) return; e.preventDefault(); try { const clip = JSON.parse(raw); const rect = innerRef.current?.getBoundingClientRect(); const start = rect ? Math.max(0, ((e.clientX - rect.left) / rect.width) * eff) : 0; onImageDrop(clip, start) } catch {} }}>
              {imageClips.map(im => clipEl('image', im, 'bg-gradient-to-r from-orange-600/40 to-amber-600/40 border border-orange-500/40 text-orange-100', '🖼'))}
              {imageClips.length === 0 && (<span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-600">drop or add B-roll images here</span>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
